import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { buildInfo } from '../buildinfo';
import { environment } from '../environments/environment';
import { BreadcrumbComponent } from './core/components/breadcrumb/breadcrumb.component';
import { PausedOverlayComponent } from './core/components/paused-overlay/paused-overlay.component';
import { UpdateOverlayComponent } from './core/components/update-overlay/update-overlay.component';
import { SampleScenarioTourService } from './core/onboarding/sample-scenario-tour.service';
import { AuthService } from './core/services/auth.service';
import { BreakpointService } from './core/services/breakpoint.service';
import { ErudaService } from './core/services/eruda.service';
import { FeedbackSoundService } from './core/services/feedback-sound.service';
import { LiveUpdateService } from './core/services/live-update.service';
import { NativeAuthService } from './core/services/native-auth.service';
import { NativeTagLinkService } from './core/services/native-tag-link.service';
import { LoggerService } from './core/services/logger.service';
import { NetworkStateService } from './core/services/network-state.service';
import { OrganizationService } from './core/services/organization.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { SwUpdateService } from './core/services/sw-update.service';
import { SyncInitService } from './core/services/sync-init.service';
import { UiStateService } from './core/services/ui-state.service';
import { UserService } from './core/services/user.service';
import { generateInitialsAvatar } from './core/utils/avatar-generator';
import { ButtonComponent } from './shared/components/button/button.component';
import { DatabaseResetDialogComponent } from './shared/components/database-reset-dialog/database-reset-dialog.component';
import { EntityListSelectorDialogComponent } from './shared/components/entity-list-selector/entity-list-selector-dialog.component';
import { IconComponent } from './shared/components/icon/icon.component';
import { ModalContainerComponent } from './shared/components/modal/modal-container.component';
import { NavListItemComponent } from './shared/components/nav-list-item/nav-list-item.component';
import { NetworkStatusBadgeComponent } from './shared/components/network-status-badge/network-status-badge.component';
import { PwaInstallModalComponent } from './shared/components/pwa-install-modal/pwa-install-modal.component';
import { SidenavComponent } from './shared/components/sidenav/sidenav.component';
import { StartupLoaderComponent } from './shared/components/startup-loader/startup-loader.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { ToolbarComponent } from './shared/components/toolbar/toolbar.component';
import { TourOverlayComponent } from './shared/components/tour-overlay/tour-overlay.component';
import { ClickOutsideDirective } from './shared/directives/click-outside.directive';
import { HasPermissionDirective } from './shared/directives/has-permission.directive';
import { TippyDirective } from './shared/directives/tippy.directive';
import { DateTimezonePipe } from './shared/pipes/date-timezone.pipe';
import { EnvPrefixPipe } from './shared/pipes/env-prefix.pipe';
import { TranslatePipe } from './shared/pipes/translate.pipe';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    ToolbarComponent,
    SidenavComponent,
    NavListItemComponent,
    IconComponent,
    ButtonComponent,
    NetworkStatusBadgeComponent,
    ToastComponent,
    TippyDirective,
    HasPermissionDirective,
    ClickOutsideDirective,
    DateTimezonePipe,
    TranslatePipe,
    EnvPrefixPipe,
    StartupLoaderComponent,
    DatabaseResetDialogComponent,
    EntityListSelectorDialogComponent,
    ModalContainerComponent,
    BreadcrumbComponent,
    PausedOverlayComponent,
    UpdateOverlayComponent,
    PwaInstallModalComponent,
    TourOverlayComponent,
  ],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.css',
})
export class App {
  protected readonly authService = inject(AuthService);
  protected readonly userService = inject(UserService);
  protected readonly organizationService = inject(OrganizationService);
  private readonly breakpointService = inject(BreakpointService); // Initialize breakpoint classes on body
  protected readonly erudaService = inject(ErudaService);
  /** Shown beside "Home" in the nav in non-production environments. */
  protected readonly envLabel = environment.artworkPath === 'artwork/production' ? null : environment.name.toLowerCase();
  private readonly loggerService = inject(LoggerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly uiState = inject(UiStateService);
  protected readonly pwaInstallService = inject(PwaInstallService); // Initialize PWA install service — exposed for template
  private readonly swUpdateService = inject(SwUpdateService); // Initialize service worker updates
  protected readonly liveUpdateService = inject(LiveUpdateService); // OTA bundle updates (native shell only) - exposed for template
  private readonly nativeAuthService = inject(NativeAuthService); // Google sign-in return link (native shell only)
  private readonly nativeTagLinkService = inject(NativeTagLinkService); // NFC tag links opening the app (native shell only)
  protected readonly syncService = inject(SyncInitService); // Initialize sync service (Phase 7: dual sync support) - exposed for template
  private readonly networkState = inject(NetworkStateService); // Check offline mode
  private readonly feedbackSound = inject(FeedbackSoundService); // "Play sound on save" form option
  private readonly sampleScenarioTour = inject(SampleScenarioTourService); // Onboarding guided tour
  private readonly logger = inject(LoggerService).getLogger('app');
  protected readonly buildTimestamp = buildInfo.timestamp;
  protected readonly title = signal('CheckPoint');
  protected readonly sidenav = viewChild.required(SidenavComponent);
  protected readonly userDropdownOpen = signal(false);
  protected readonly streamLogsEnabled = signal(this.loggerService.isStreamLogsEnabled());
  readonly initializing = signal(true); // Public for testing
  readonly initializationMessage = signal<string | null>(null); // Sync progress message
  // Startup loader during a slow sync start (e.g. first sync on a new device). Delayed so
  // the usual sub-second start on a warm database doesn't flash the loader.
  protected readonly showSyncLoader = signal(false);
  private readonly SYNC_LOADER_DELAY_MS = 400;
  protected readonly showDatabaseResetDialog = signal(false); // Managed via effect on sync service

  private readonly navHistoryLogger = inject(LoggerService).getLogger('nav-history');

  // Error routes that should redirect to home on direct access
  // NOTE: /error is intentionally included — on app boot (e.g. after a service worker refresh),
  // a stale /error URL should not immediately re-render the error page. Instead we redirect to /
  // and let the resolvers run fresh. If they succeed, the user recovers transparently.
  // If they fail again, /error will be shown as normal.
  private readonly errorRoutes = ['/error', '/sync-error', '/forbidden', '/browser-error'];

  constructor() {
    // CRITICAL: Log environment config IMMEDIATELY to debug sync service selection
    this.logger.info('🔍 === APP CONSTRUCTOR START ===');
    this.logger.info('🌍 Environment name:', environment.name);
    this.logger.info('🌍 Environment production:', environment.production);
    this.logger.info('🌍 Environment API URL:', environment.apiUrl);

    // Native shell: confirm this web bundle rendered (cancels OTA rollback), then check for updates
    afterNextRender(() => void this.liveUpdateService.start());

    // Native shell: catch the Google sign-in return link (also when it cold-starts the app)
    void this.nativeAuthService.init();

    // Native shell: open NFC tag links that launch or reach the app outside the scan page
    void this.nativeTagLinkService.init();

    // Redirect away from error pages if accessed directly on app load
    const browserUrl = window.location?.pathname ?? '/';
    const routerUrl = this.router.url;
    this.logger.debug(`🚀 App constructor - browser URL: ${browserUrl}, router URL: ${routerUrl}`);
    if (this.errorRoutes.some((route) => browserUrl.startsWith(route))) {
      this.logger.debug(`⚠️ Error page accessed directly: ${browserUrl} - redirecting to /`);
      window.history?.replaceState?.(null, '', '/');
      this.router.navigate(['/'], { replaceUrl: true });
    }

    // Show the startup loader while sync start() holds navigation for longer than a moment
    effect((onCleanup) => {
      if (!this.syncService.starting()) {
        this.showSyncLoader.set(false);
        return;
      }
      const timer = setTimeout(() => this.showSyncLoader.set(true), this.SYNC_LOADER_DELAY_MS);
      onCleanup(() => clearTimeout(timer));
    });

    // Unlock Web Audio on the first tap so later saves (even auto-submitted ones) can chime
    this.feedbackSound.installUnlockListeners();

    // Resume an onboarding tour interrupted by a reload, once signed in and routed
    let tourResumeAttempted = false;
    effect(() => {
      if (tourResumeAttempted || this.initializing() || !this.authService.isAuthenticated()) {
        return;
      }
      tourResumeAttempted = true;
      void this.sampleScenarioTour.resume();
    });

    // Sync database reset dialog visibility with sync service
    effect(() => {
      this.showDatabaseResetDialog.set(this.syncService.showResetDialog());
    });

    // Log environment configuration at app startup
    this.logger.info('=== CheckPoint Environment Configuration ===');
    this.logger.info('Production:', environment.production);
    this.logger.info('API URL:', environment.apiUrl);
    this.logger.info('OAuth Redirect URI:', environment.oauth.google.redirectUri);
    this.logger.info('OAuth Client ID:', environment.oauth.google.clientId);
    this.logger.info('Build:', buildInfo.timestamp, 'Git:', buildInfo.gitHash ?? 'unknown');
    this.logger.info('PWA Install Available:', this.pwaInstallService.canInstall() !== null);
    this.logger.info('PWA Installed:', this.pwaInstallService.installed());
    this.logger.info('Detected Platform:', this.pwaInstallService.detectedPlatform());
    this.logger.info('==========================================');

    // Database will initialize lazily on first use after login
    // Each database operation calls init() automatically and handles auth requirements

    // Clear external navigation history to prevent back button from leaving app
    this.initializeNavigationHistory();

    // Sync sidenav state from query params
    this.route.queryParams.subscribe((params) => {
      const shouldBeOpen = params['n'] === '1';
      this.navHistoryLogger.debug('🔄 Query param changed', {
        n: params['n'],
        shouldBeOpen,
        currentlyOpen: this.uiState.sidenavOpen(),
      });
      this.uiState.setSidenavState(shouldBeOpen);
    });

    // Log all route changes with global [ROUTE-CHANGE] prefix for visibility in E2E tests
    let previousUrl = '/';
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.navHistoryLogger.debug(
          `[ROUTE-CHANGE] FROM: ${previousUrl} → TO: ${event.urlAfterRedirects}`,
        );
        this.navHistoryLogger.info('🔀 Route changed', {
          from: previousUrl,
          to: event.urlAfterRedirects,
          url: event.url,
          historyLength: window.history.length,
        });

        // Update previousUrl for next navigation
        previousUrl = event.urlAfterRedirects;

        // Hide loader after navigation completes
        // NavigationEnd fires AFTER all guards and resolvers complete, so route is fully loaded
        this.logger.debug(`📍 Route loaded: ${event.urlAfterRedirects} - hiding loader`);
        this.initializing.set(false);
      });

    // Note: App initialization is now handled by resolvers in route configuration
    // UserResolver and OrganizationResolver load data before protected routes activate
    // This eliminates race conditions and simplifies the initialization flow
    this.logger.info('✅ App constructor complete - data loading delegated to resolvers');
  }

  logout(): void {
    this.authService.logout();
    this.userDropdownOpen.set(false);
  }

  toggleUserDropdown(): void {
    this.userDropdownOpen.update((v) => !v);
  }

  protected closeUserDropdown(): void {
    this.userDropdownOpen.set(false);
  }

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  /**
   * User shown in the header/sidenav. UserService loads from the local DB only
   * after the first sync, so fall back to the profile AuthService fetched at sign-in.
   */
  protected readonly displayUser = computed(
    () => this.userService.currentUser() ?? this.authService.currentUser(),
  );

  /**
   * Get user avatar URL with fallback to generated initials avatar
   */
  getUserAvatar(): string {
    const user = this.displayUser();
    if (!user) return '';

    return user.avatar_url || generateInitialsAvatar(user.email);
  }

  /**
   * Toggle continuous log streaming to the backend on/off.
   */
  toggleStreamLogs(): void {
    const next = !this.loggerService.isStreamLogsEnabled();
    this.loggerService.setStreamLogsEnabled(next);
    this.streamLogsEnabled.set(next);
  }

  /**
   * Toggle log streaming and close the user dropdown
   */
  toggleStreamLogsAndCloseDropdown(): void {
    this.userDropdownOpen.set(false);
    this.toggleStreamLogs();
  }

  /**
   * Handle database reset dialog response
   * Called when user chooses to reset or keep local data
   * Custom sync doesn't support reset dialog - this is a no-op
   */
  async handleDatabaseReset(shouldReset: boolean): Promise<void> {
    this.logger.info('Database reset choice:', shouldReset ? 'Reset & Re-sync' : 'Keep Local Data');
    this.logger.warn('Reset dialog not implemented for custom sync');
  }

  /**
   * Initialize navigation history to prevent back button from leaving the app
   * Replaces any external referrer with the home page
   */
  private initializeNavigationHistory(): void {
    // Check if user came from external site (different origin)
    const referrer = window.document.referrer;
    const currentOrigin = window.location.origin;

    // If referrer is external or empty, ensure home is first in window.history
    if (!referrer || !referrer.startsWith(currentOrigin)) {
      this.logger.debug('External referrer detected, clearing navigation history');

      // Replace the current history entry with home page
      // This ensures clicking back doesn't navigate to external site
      const currentPath = window.location?.pathname ?? '/';
      const currentSearch = window.location?.search ?? '';
      const currentUrl = currentPath + currentSearch;

      if (currentPath !== '/' && currentPath !== '') {
        // User landed on a deep link - push home first, then current page
        // This makes back button go to home instead of external site
        // IMPORTANT: Preserve query params (e.g., OAuth callback token)
        window.history?.replaceState?.(null, '', '/');
        window.history?.pushState?.(null, '', currentUrl);
      } else {
        // Already on home - just ensure it's the first entry
        window.history?.replaceState?.(null, '', currentUrl);
      }
    }
  }
}
